import React, { useState, useEffect } from 'react';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Calendar, CheckCircle, XCircle, BookOpen, Users, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function Attendance() {
    const { fetchMyMappings, fetchAttendance } = useData();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [mappings, setMappings] = useState([]);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [classStatuses, setClassStatuses] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user?.id) return;

        const loadInitialData = async () => {
            setLoading(true);
            try {
                // Fetch REAL mappings for this user
                const myMappings = await fetchMyMappings(user.id);
                setMappings(myMappings);

                await loadDailyStatus(myMappings);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, [user, date]);

    const loadDailyStatus = async (currentMappings) => {
        const statuses = {};
        await Promise.all(currentMappings.map(async (m) => {
            const records = await fetchAttendance(m.id, date);
            if (records && records.length > 0) {
                const present = records.filter(r => r.status === 'PRESENT').length;
                statuses[m.id] = {
                    status: 'MARKED',
                    present: present,
                    total: records.length,
                    absent: records.length - present
                };
            } else {
                statuses[m.id] = { status: 'PENDING', present: 0, total: 0 };
            }
        }));
        setClassStatuses(statuses);
    };

    const stats = {
        totalClasses: mappings.length,
        marked: Object.values(classStatuses).filter(s => s.status === 'MARKED').length,
        pending: Object.values(classStatuses).filter(s => s.status === 'PENDING').length
    };

    if (loading && mappings.length === 0) return (
        <div className="min-h-[400px] flex flex-col items-center justify-center gap-4">
            <div className="h-10 w-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
            <p className="text-muted-foreground font-medium text-sm font-mono tracking-tighter uppercase">Synchronizing Records...</p>
        </div>
    );

    return (
        <div className="space-y-6 animate-in fade-in duration-700">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black font-heading text-primary tracking-tight">Attendance Hub</h1>
                    <p className="text-muted-foreground font-medium text-sm">Manage class registries for {user?.name}</p>
                </div>

                <div className="flex items-center gap-3 bg-secondary/50 p-2.5 rounded-2xl border border-border/50 shadow-sm">
                    <Calendar size={18} className="text-primary ml-1" />
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="bg-transparent border-none outline-none text-sm font-bold text-foreground"
                    />
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card p-6 rounded-2xl border border-border shadow-sm flex items-center justify-between group">
                    <div>
                        <p className="text-[10px] text-muted-foreground font-black uppercase tracking-widest mb-1">Total Classes</p>
                        <p className="text-3xl font-black text-foreground">{stats.totalClasses}</p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform">
                        <BookOpen size={24} />
                    </div>
                </div>
                <div className="bg-card p-6 rounded-2xl border border-border shadow-sm flex items-center justify-between group">
                    <div>
                        <p className="text-[10px] text-muted-foreground font-black uppercase tracking-widest mb-1">Marked Today</p>
                        <p className="text-3xl font-black text-emerald-500">{stats.marked}</p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <CheckCircle size={24} />
                    </div>
                </div>
                <div className="bg-card p-6 rounded-2xl border border-border shadow-sm flex items-center justify-between group">
                    <div>
                        <p className="text-[10px] text-muted-foreground font-black uppercase tracking-widest mb-1">Pending Sync</p>
                        <p className="text-3xl font-black text-orange-500">{stats.pending}</p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-orange-500/10 text-orange-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <AlertCircle size={24} />
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {mappings.map(m => {
                    const status = classStatuses[m.id] || { status: 'PENDING' };
                    return (
                        <div
                            key={m.id}
                            onClick={() => navigate(`/dashboard/attendance/${m.id}`)}
                            className="bg-card border border-border rounded-[2rem] p-6 hover:shadow-xl hover:shadow-primary/5 transition-all cursor-pointer group relative overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full blur-3xl -mr-12 -mt-12"></div>

                            <div className="flex justify-between items-start mb-6">
                                <div className="space-y-1">
                                    <h3 className="font-black text-lg text-foreground group-hover:text-primary transition-colors leading-tight">{m.subjectName}</h3>
                                    <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground bg-secondary/30 w-fit px-2 py-1 rounded-lg">
                                        <Users size={14} className="text-primary" />
                                        <span>{m.sectionName}</span>
                                    </div>
                                </div>
                                {status.status === 'MARKED' ? (
                                    <span className="bg-emerald-500/10 text-emerald-600 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 border border-emerald-500/20">
                                        <CheckCircle size={14} /> Marked
                                    </span>
                                ) : (
                                    <span className="bg-orange-500/10 text-orange-600 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 border border-orange-500/20">
                                        <AlertCircle size={14} /> Pending
                                    </span>
                                )}
                            </div>

                            {status.status === 'MARKED' ? (
                                <div className="space-y-3">
                                    <div className="flex justify-between text-[10px] font-black uppercase tracking-widest">
                                        <span className="text-emerald-500">Stability: {Math.round((status.present / status.total) * 100)}%</span>
                                        <span className="text-muted-foreground">{status.present} / {status.total} Strength</span>
                                    </div>
                                    <div className="h-2.5 w-full bg-secondary rounded-full overflow-hidden p-0.5">
                                        <div
                                            className="h-full bg-emerald-500 rounded-full transition-all duration-700 shadow-sm"
                                            style={{ width: `${(status.present / status.total) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-4 flex items-center gap-2 text-xs font-bold text-muted-foreground/60 italic">
                                    <AlertCircle size={14} /> Open registry to mark daily presence
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {mappings.length === 0 && !loading && (
                <div className="text-center py-20 bg-muted/20 border border-dashed border-border rounded-[2rem]">
                    <div className="h-16 w-16 bg-background rounded-full flex items-center justify-center mx-auto mb-4 border border-border">
                        <BookOpen size={32} className="text-muted-foreground/30" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground">No Academic Assignments</h3>
                    <p className="text-sm text-muted-foreground max-w-xs mx-auto mt-1 font-medium">Your faculty account doesn't have any class mappings assigned yet.</p>
                </div>
            )}
        </div>
    );
}
